import { Component, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth/auth.service';
import { UsersService } from 'src/app/mods/users/users.service';
import { OverlayUsersService } from 'src/app/shared/collaboration/overlay-users.service';

interface ProfileFields {
  profileTitle: string;
  profileStatus: string;
  profileSupport: string;
  profileAbout: string;
}

@Component({
  standalone: false,
  selector: 'app-user-profile-dialog',
  templateUrl: './user-profile-dialog.component.html',
  styleUrls: ['./user-profile-dialog.component.scss'],
})
export class UserProfileDialogComponent {
  private readonly config = inject(DynamicDialogConfig);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly authService = inject(AuthService);
  private readonly usersService = inject(UsersService);
  private readonly overlayUsers = inject(OverlayUsersService);
  private readonly translate = inject(TranslateService);

  readonly currentUser = this.authService.user.value || this.authService.getUserFromLocalStorage();
  readonly user: any = this.config.data?.user || this.currentUser || {};
  readonly userId = Number(this.user.userId || this.user.id || 0);
  readonly isOwnProfile = this.userId > 0
    && this.userId === Number(this.currentUser?.id || 0);
  readonly avatar = this.user.avatar || this.user.avatarUrl || '../../../assets/icons/icon-sbest-512x512.png';
  readonly name = this.user.name || 'Usuario';
  readonly email = this.user.email || '';
  readonly department = this.user.department || this.user._dpto || 'Sin departamento';

  profile: ProfileFields = {
    profileTitle: this.user.profileTitle || '',
    profileStatus: this.user.profileStatus || '',
    profileSupport: this.user.profileSupport || '',
    profileAbout: this.user.profileAbout || '',
  };
  saving = false;
  saveError = '';

  save(): void {
    if (!this.isOwnProfile || this.saving) {
      return;
    }

    this.saving = true;
    this.saveError = '';
    const profile: ProfileFields = {
      profileTitle: this.profile.profileTitle.trim(),
      profileStatus: this.profile.profileStatus.trim(),
      profileSupport: this.profile.profileSupport.trim(),
      profileAbout: this.profile.profileAbout.trim(),
    };

    this.usersService.updateMyProfile(profile).subscribe({
      next: (response) => {
        const user = response?.user || profile;
        const updatedUser = this.authService.updateStoredUser(user) || user;
        const updatedProfile = {
          profileTitle: updatedUser.profileTitle ?? profile.profileTitle,
          profileStatus: updatedUser.profileStatus ?? profile.profileStatus,
          profileSupport: updatedUser.profileSupport ?? profile.profileSupport,
          profileAbout: updatedUser.profileAbout ?? profile.profileAbout,
        };
        this.overlayUsers.updateUserProfile(this.userId, updatedProfile);
        this.dialogRef.close({ ...updatedUser, ...updatedProfile });
      },
      error: (error: unknown) => {
        const responseError = error as { error?: { msg?: string }; message?: string };
        this.saveError = typeof error === 'string'
          ? error
          : responseError?.error?.msg || responseError?.message || this.translate.instant('USER.PROFILE.SAVE_ERROR');
        this.saving = false;
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
